// Mark as Non-Applicable Modal
// Allows users to exempt an asset from a compliance rule with proper justification

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, Upload, X, FileText, Loader2, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// Exemption reasons
const EXEMPTION_REASONS = [
  { value: 'AssetDecommissioned', label: 'Asset Decommissioned' },
  { value: 'AssetSold', label: 'Asset Sold' },
  { value: 'AssetDisposed', label: 'Asset Disposed' },
  { value: 'IncorrectlyCategorized', label: 'Incorrectly Categorized' },
  { value: 'RegulatoryExemption', label: 'Regulatory Exemption' },
  { value: 'Other', label: 'Other' },
] as const;

// Mock reviewers - in real app, this would come from API
const MOCK_REVIEWERS = [
  { value: '1', label: 'John Smith (Compliance Manager)' },
  { value: '2', label: 'Sarah Johnson (Operations Director)' },
  { value: '3', label: 'Mike Williams (Asset Manager)' },
];

// Form validation schema
const formSchema = z.object({
  reason: z.string().min(1, 'Reason is required'),
  detailedExplanation: z.string()
    .min(50, 'Detailed explanation must be at least 50 characters'),
  effectiveDate: z.date({
    message: 'Effective date is required',
  }).refine((date) => date <= new Date(), {
    message: 'Effective date cannot be in the future',
  }),
  expiryDate: z.date().optional(),
  reviewedBy: z.string().optional(),
}).refine((data) => {
  // Expiry date must be after effective date if provided
  if (data.expiryDate && data.effectiveDate) {
    return data.expiryDate > data.effectiveDate;
  }
  return true;
}, {
  message: 'Expiry date must be after effective date',
  path: ['expiryDate'],
});

type FormValues = z.infer<typeof formSchema>;

interface MarkNonApplicableModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  complianceItem?: {
    id: number;
    assetName: string;
    regimeName: string;
  };
  onSubmit: (data: FormValues & { files: File[] }) => Promise<void>;
}

export function MarkNonApplicableModal({
  open,
  onOpenChange,
  complianceItem,
  onSubmit,
}: MarkNonApplicableModalProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      reason: '',
      detailedExplanation: '',
      effectiveDate: new Date(),
      expiryDate: undefined,
      reviewedBy: '',
    },
  });

  const selectedReason = form.watch('reason');
  const requiresDocumentation = selectedReason === 'RegulatoryExemption';

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    setFiles((prev) => [...prev, ...selectedFiles]);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (data: FormValues) => {
    // Validate documentation for regulatory exemption
    if (requiresDocumentation && files.length === 0) {
      toast.error('Supporting documentation is required for Regulatory Exemption');
      return;
    }

    try {
      setIsSubmitting(true);
      await onSubmit({ ...data, files });
      toast.success('Compliance item marked as non-applicable');
      form.reset();
      setFiles([]);
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to mark as non-applicable');
      console.error('Error marking as non-applicable:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Mark as Non-Applicable</DialogTitle>
          <DialogDescription>
            {complianceItem ? (
              <>
                Exempt <strong>{complianceItem.assetName}</strong> from {complianceItem.regimeName}
              </>
            ) : (
              'Exempt an asset from a compliance rule with proper justification'
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Warning Alert */}
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            This will remove the compliance item from active tracking. The item will still be visible in history for audit purposes.
          </AlertDescription>
        </Alert>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {/* Reason */}
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reason *</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a reason" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {EXEMPTION_REASONS.map((reason) => (
                        <SelectItem key={reason.value} value={reason.value}>
                          {reason.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Detailed Explanation */}
            <FormField
              control={form.control}
              name="detailedExplanation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Detailed Explanation *</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Provide a detailed explanation for this exemption (minimum 50 characters)"
                      className="min-h-[120px]"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {field.value.length}/50 characters minimum
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Effective Date */}
            <FormField
              control={form.control}
              name="effectiveDate"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Effective Date *</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-full pl-3 text-left font-normal',
                            !field.value && 'text-muted-foreground'
                          )}
                        >
                          {field.value ? (
                            format(field.value, 'PPP')
                          ) : (
                            <span>Pick a date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        disabled={(date) => date > new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormDescription>
                    The date from which this exemption is effective (defaults to today)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Expiry Date (Optional) */}
            <FormField
              control={form.control}
              name="expiryDate"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Expiry Date (Optional)</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          className={cn(
                            'w-full pl-3 text-left font-normal',
                            !field.value && 'text-muted-foreground'
                          )}
                        >
                          {field.value ? (
                            format(field.value, 'PPP')
                          ) : (
                            <span>Pick a date (optional)</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormDescription>
                    For temporary exemptions, set an expiry date
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Reviewed By */}
            <FormField
              control={form.control}
              name="reviewedBy"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reviewed By (Optional)</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a reviewer" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {MOCK_REVIEWERS.map((reviewer) => (
                        <SelectItem key={reviewer.value} value={reviewer.value}>
                          {reviewer.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Optionally select who reviewed and approved this exemption
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* File Upload */}
            <div className="space-y-2">
              <FormLabel>
                Supporting Documentation {requiresDocumentation && '*'}
              </FormLabel>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('file-upload')?.click()}
                  className="w-full"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Upload Files
                </Button>
              </div>
              {requiresDocumentation && (
                <p className="text-sm text-muted-foreground">
                  Required for Regulatory Exemption
                </p>
              )}
              {files.length > 0 && (
                <div className="space-y-2 mt-2">
                  {files.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-muted rounded-md"
                    >
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{file.name}</span>
                        <span className="text-xs text-muted-foreground">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFile(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} variant="destructive">
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Mark as Non-Applicable
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
